/**
 * Vertical histogram of review grades 1-5. Each bar is colour-coded so
 * the operator can immediately see whether a 4.3 average is "lots of 5s
 * with a few 1s" or "consistent 4-5s".
 *
 * Header surfaces the live average alongside a 5-star strip.
 */
import { useMemo } from "react";

import ChartCard from "../ChartCard/ChartCard";
import Stars from "../Stars/Stars";
import { RATING_BAR_COLOR } from "../../../../../constants/analytics.constants";
import { RatingDistributionStyles } from "./ratingDistribution.styles";

const RatingDistribution = ({ data = [] }) => {
  const total = useMemo(
    () => data.reduce((s, d) => s + d.count, 0),
    [data]
  );
  const avg = useMemo(() => {
    if (total === 0) return 0;
    return data.reduce((s, d) => s + d.grade * d.count, 0) / total;
  }, [data, total]);
  const max = Math.max(1, ...data.map(d => d.count));

  return (
    <ChartCard
      title="Rating distribution"
      subtitle={`${total.toLocaleString()} reviews`}
      action={
        total > 0 ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: "#292359", fontVariantNumeric: "tabular-nums" }}>
              {avg.toFixed(2)}
            </span>
            <Stars value={avg} size={11} />
          </span>
        ) : null
      }
    >
      {total === 0 ? (
        <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#8E8E8E", fontSize: 12 }}>
          No reviews yet
        </div>
      ) : (
        <RatingDistributionStyles>
          {data.map(d => {
            const pct = (d.count / max) * 100;
            const share = total ? Math.round((d.count / total) * 100) : 0;
            return (
              <div key={d.grade} className="histo-col">
                <div className="histo-bar-wrap">
                  <div
                    className="histo-bar"
                    style={{
                      height: `${pct}%`,
                      background: RATING_BAR_COLOR[d.grade] || "#7F7B9B"
                    }}
                  >
                    <span className="histo-count">{d.count}</span>
                  </div>
                </div>
                <div className="histo-label">
                  <span className="histo-grade">
                    {d.grade}
                    <span className="histo-star" aria-hidden="true">★</span>
                  </span>
                  <span className="histo-share">{share}%</span>
                </div>
              </div>
            );
          })}
        </RatingDistributionStyles>
      )}
    </ChartCard>
  );
};

export default RatingDistribution;
